import React, { useState, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import * as yup from "yup";
import { Formik } from "formik";
import { Form as FormFormik } from "formik";
import InputMask from "react-input-mask";
import { Col, Row } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import TextField from "@mui/material/TextField";
import { BsCreditCard2Front } from "react-icons/bs";
import { useApp } from "../../contexts/AppContext";

const validationSchema = yup.object({
  cep: yup.string().required("CEP obrigatório"),
  streetName: yup.string().required("Endereço obrigatório"),
  number: yup.string().required("Número obrigatório"),
  complement: yup.string().notRequired(),
});

export default function CardAddressForm({ onConfirm, isSaving }) {
  const { whitelabel } = useApp();
  const [loadingCep, setLoadingCep] = useState(false);
  const autoFilled = useRef({ city: "", neighborhood: "", state: "" });
  const debounceTimerRef = useRef(null);

  const primaryColor = whitelabel?.theme?.text_primary_color;
  const standardSx = {
    "& .MuiInput-underline:before": { borderBottomColor: primaryColor },
    "& .MuiInput-underline:after": { borderBottomColor: primaryColor },
    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
      borderBottomColor: primaryColor,
    },
    "& .MuiInputBase-input": { color: primaryColor },
  };

  const getAddressByCep = useCallback((cep, setFieldValue, setFieldError) => {
    setFieldValue("cep", cep);

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    const cleanCep = cep.replace(/\D/g, "");
    if (cleanCep.length < 8) {
      if (cleanCep.length > 0) setFieldError("cep", "CEP inválido");
      return;
    }

    setLoadingCep(true);
    debounceTimerRef.current = setTimeout(() => {
      fetch(`https://viacep.com.br/ws/${cleanCep}/json`)
        .then((res) => res.json())
        .then((data) => {
          if (data.erro) {
            setFieldError("cep", "CEP inválido");
            setLoadingCep(false);
            return;
          }
          setFieldError("cep", "");
          setFieldValue("streetName", data.logradouro || "");
          autoFilled.current = {
            city: data.localidade || "",
            neighborhood: data.bairro || "",
            state: data.uf || "",
          };
          setLoadingCep(false);
        })
        .catch(() => {
          setFieldError("cep", "CEP inválido");
          setLoadingCep(false);
        });
    }, 500);
  }, []);

  const initialValues = {
    cep: "",
    streetName: "",
    number: "",
    complement: "",
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={async (values, actions) => {
        try {
          const payload = {
            label: "Endereço principal",
            streetName: values.streetName.trim(),
            number: String(values.number).trim(),
            complement: values.complement?.trim() || "",
            zipCode: values.cep.replace(/\D/g, ""),
            neighborhood: autoFilled.current.neighborhood,
            city: autoFilled.current.city,
            state: autoFilled.current.state,
            main: true,
            recipient: "",
            reference: "",
          };
          await onConfirm(payload);
        } catch (error) {
        } finally {
          actions.setSubmitting(false);
        }
      }}
    >
      {({
        values,
        errors,
        handleChange,
        handleBlur,
        setFieldValue,
        setFieldError,
        isSubmitting,
      }) => (
        <FormFormik className="mb-4">
          <Form.Group className="mb-3">
            <span className="form-label mb-1">CEP</span>
            <InputMask
              mask="99999-999"
              maskChar={null}
              name="cep"
              value={values.cep}
              onChange={(e) =>
                getAddressByCep(e.target.value, setFieldValue, setFieldError)
              }
              onBlur={handleBlur}
            >
              {(inputProps) => (
                <TextField
                  {...inputProps}
                  variant="standard"
                  type="tel"
                  fullWidth
                  InputProps={{
                    endAdornment: <BsCreditCard2Front />,
                    inputMode: "numeric",
                  }}
                  sx={standardSx}
                />
              )}
            </InputMask>
            {errors.cep && (
              <span className="text-danger text-sm">{errors.cep}</span>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <span className="form-label mb-1">Endereço</span>
            <TextField
              className="form-control"
              name="streetName"
              variant="standard"
              value={values.streetName}
              onChange={handleChange}
              onBlur={handleBlur}
              sx={standardSx}
            />
            {errors.streetName && (
              <span className="text-danger text-sm">{errors.streetName}</span>
            )}
          </Form.Group>

          <Row>
            <Col>
              <Form.Group className="mb-3">
                <span className="form-label mb-1">Número</span>
                <TextField
                  className="form-control"
                  name="number"
                  variant="standard"
                  value={values.number}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  sx={standardSx}
                />
                {errors.number && (
                  <span className="text-danger text-sm">{errors.number}</span>
                )}
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <span className="form-label mb-1">Complemento</span>
                <TextField
                  className="form-control"
                  name="complement"
                  variant="standard"
                  value={values.complement}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  sx={standardSx}
                />
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex justify-content-center my-4">
            <button
              type="submit"
              className="btn-default-wl font-wl-700 text-xxs p-2"
              style={{ width: 187 }}
              disabled={isSubmitting || isSaving}
            >
              {isSubmitting || isSaving ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                />
              ) : (
                "AVANÇAR"
              )}
            </button>
          </div>
        </FormFormik>
      )}
    </Formik>
  );
}

CardAddressForm.propTypes = {
  onConfirm: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
};
